// src/components/AudioVisualization.tsx

import React, { useEffect, useRef } from 'react';
import { useRealtimeService } from '../contexts/RealtimeServiceContext';
import { WavRenderer } from '../utils/wav_renderer';
import './AudioVisualization.scss';

interface AudioVisualizationProps {

}

export const AudioVisualization: React.FC<AudioVisualizationProps> = ({
    
}) => {

    const { wavRecorderRef, wavStreamPlayerRef } = useRealtimeService();
    const USE_BARS = true

    /**
     * References for
     * - Rendering audio visualization (canvas)
     * - Autoscrolling event logs
     * - Timing delta for event log displays
     */
    const clientCanvasRef = useRef<HTMLCanvasElement>(null);
    const serverCanvasRef = useRef<HTMLCanvasElement>(null);

    /**
     * Set up render loops for the visualization canvas
     */
    useEffect(() => {
        let isLoaded = true;

        let clientCtx: CanvasRenderingContext2D | null = null;
        let serverCtx: CanvasRenderingContext2D | null = null;

        const render = () => {
            const wavRecorder = wavRecorderRef.current;
            const wavStreamPlayer = wavStreamPlayerRef.current;

            if (isLoaded) {
                if (clientCanvasRef.current) {
                    const clientCanvas = clientCanvasRef.current;
                    if (!clientCanvas.width || !clientCanvas.height) {
                        clientCanvas.width = clientCanvas.offsetWidth;
                        clientCanvas.height = clientCanvas.offsetHeight;
                    }
                    clientCtx = clientCtx || clientCanvas.getContext('2d');
                    if (clientCtx) {
                        clientCtx.clearRect(0, 0, clientCanvas.width, clientCanvas.height);
                        const result = wavRecorder.recording
                            ? wavRecorder.getFrequencies('voice')
                            : { values: new Float32Array([0]) };
                        if (USE_BARS) {
                            WavRenderer.drawBars(
                                clientCanvas,
                                clientCtx,
                                result.values,
                                '#3c00ff',
                                10,
                                0,
                                8
                            );
                        }
                        else {
                            WavRenderer.drawCurve(
                                clientCanvas,
                                clientCtx,
                                result.values,
                                '#3c00ff',  // color
                                10,         // pointCount
                                14,         // lineWidth
                                4,          // amplitude
                            );
                        }
                    }
                }
                if (serverCanvasRef.current) {
                    const serverCanvas = serverCanvasRef.current;
                    if (!serverCanvas.width || !serverCanvas.height) {
                        serverCanvas.width = serverCanvas.offsetWidth;
                        serverCanvas.height = serverCanvas.offsetHeight;
                    }
                    serverCtx = serverCtx || serverCanvas.getContext('2d');
                    if (serverCtx) {
                        serverCtx.clearRect(0, 0, serverCanvas.width, serverCanvas.height);
                        const result = wavStreamPlayer.analyser
                            ? wavStreamPlayer.getFrequencies('voice')
                            : { values: new Float32Array([0]) };
                        if (USE_BARS) {
                            WavRenderer.drawBars(
                                serverCanvas,
                                serverCtx,
                                result.values,
                                '#0099ff',
                                10,
                                0,
                                8
                            );
                        }
                        else {
                            WavRenderer.drawCurve(
                                serverCanvas,
                                serverCtx,
                                result.values,
                                '#0099ff', // color
                                10,        // pointCount
                                14,        // lineWidth
                                4,         // amplitude
                            );
                        }


                    }
                }
                window.requestAnimationFrame(render);
            }
        };
        render();

        return () => {
            isLoaded = false;
        };
    }, [wavRecorderRef.current, wavStreamPlayerRef.current]);

    return (
        <div className={`visualization ${USE_BARS ? '' : 'line'}`}>
            <div className={`visualization-entry ${USE_BARS ? '' : 'line'} client`}>
                <canvas ref={clientCanvasRef} />
            </div>
            <div className={`visualization-entry ${USE_BARS ? '' : 'line'} server`}>
                <canvas ref={serverCanvasRef} />
            </div>
        </div>
    );
};